// src/components/UpdateAvailableBanner/UpdateAvailableBanner.tsx
// Bannière pour notifier l'utilisateur qu'une nouvelle version de l'app est disponible

import React, { useState, useEffect } from 'react';
import { Alert, Button, Space } from 'antd';
import { ReloadOutlined, CloseOutlined } from '@ant-design/icons';
import { useAppTranslation } from '../../hooks/useAppTranslation';

interface UpdateAvailableBannerProps {
  onUpdate: () => void;
  onDismiss?: () => void;
}

const UpdateAvailableBanner: React.FC<UpdateAvailableBannerProps> = ({ onUpdate, onDismiss }) => {
  const { t } = useAppTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [autoReloadCountdown, setAutoReloadCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (!isVisible) return;

    let countdown = 30;
    setAutoReloadCountdown(countdown);

    const interval = setInterval(() => {
      countdown -= 1;
      setAutoReloadCountdown(countdown);

      if (countdown <= 0) {
        clearInterval(interval);
        onUpdate();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible, onUpdate]);

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (!isVisible) {
    return null;
  }

  const countdownKey =
    autoReloadCountdown !== null && autoReloadCountdown > 1
      ? 'update.countdown_plural'
      : 'update.countdown';

  return (
    <Alert
      type="warning"
      showIcon
      message={
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>🔄 {t('update.title')}</strong>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={handleDismiss}
              style={{ marginLeft: 16 }}
            />
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            {autoReloadCountdown !== null && autoReloadCountdown > 0 ? (
              <>{t(countdownKey, { count: autoReloadCountdown })}</>
            ) : (
              <>{t('update.updating')}</>
            )}
          </div>
        </Space>
      }
      action={
        <Button
          type="primary"
          size="small"
          icon={<ReloadOutlined />}
          onClick={onUpdate}
        >
          {t('actions.updateNow')}
        </Button>
      }
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        maxWidth: '90%',
        width: '600px',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
      closable={false}
    />
  );
};

export default UpdateAvailableBanner;
